import {useMemo} from 'react';

import {useUserDynamicConfigWithLoading} from '../useDynamicConfig';

export type LinePlotPointDensityConfig = {
  useCustomBreakpoints: boolean;
  breakpoints: Record<number, number>;
};

// Prepared once per config change and shared by all panels, in descending order.
export type PreparedBreakpoints = ReadonlyArray<
  readonly [threshold: number, bins: number]
>;

export const DEFAULT_LINE_PLOT_BREAKPOINTS: PreparedBreakpoints = [
  [500, 1000],
  [400, 800],
  [300, 600],
  [200, 500],
  [0, 400],
];

export const useLinePlotPointDensityBreakpoints = (): PreparedBreakpoints => {
  const {config} = useUserDynamicConfigWithLoading('history_line_breakpoints');
  return useMemo(
    function prepareBreakpoints() {
      const typedConfig = config as
        | Partial<LinePlotPointDensityConfig>
        | undefined;
      if (typedConfig?.useCustomBreakpoints !== true) {
        return DEFAULT_LINE_PLOT_BREAKPOINTS;
      }
      const breakpoints = typedConfig.breakpoints;
      if (
        breakpoints == null ||
        typeof breakpoints !== 'object' ||
        Array.isArray(breakpoints)
      ) {
        return DEFAULT_LINE_PLOT_BREAKPOINTS;
      }
      const entries = Object.entries(breakpoints);
      // Reject malformed remote values before they can become query bin counts.
      if (
        entries.length === 0 ||
        !entries.every(
          ([width, bins]) =>
            width.trim() !== '' &&
            Number.isFinite(Number(width)) &&
            Number(width) >= 0 &&
            Number.isSafeInteger(bins) &&
            bins > 0
        )
      ) {
        return DEFAULT_LINE_PLOT_BREAKPOINTS;
      }
      return entries
        .map(([width, bins]): [number, number] => [Number(width), bins])
        .sort(([a], [b]) => b - a);
    },
    [config]
  );
};
